import { TFunction } from 'i18next';
import {
    BundleCardType,
    BundleSection,
} from '../components/BundledDocuments/BundledDocuments.types';

export interface DocumentSetCategory {
    key: string;
    name: string;
    orderIndex: number;
}

export interface DocumentSet {
    id: number;
    key: string;
    name: string;
    productId: number;
    process: number;
    categories: DocumentSetCategory[];
}

export interface DocumentSetsResponse {
    documentSets: DocumentSet[];
}

export interface ProductOption {
    value: string;
    label: string;
}

// process coming from the API mapped to the internal process/section metadata.
const PROCESS_CONFIG: Record<
    number,
    { processType: string; sectionKey: string; sectionFallback: string; subtitleKey: string; subtitleFallback: string }
> = {
    1: {
        processType: 'create',
        sectionKey: 'bundledDocuments.createNewPlan',
        sectionFallback: 'Create new plan',
        subtitleKey: 'bundledDocuments.productOpening',
        subtitleFallback: ' product opening',
    },
    2: {
        processType: 'change',
        sectionKey: 'bundledDocuments.changeExistingPlan',
        sectionFallback: 'Change existing plan',
        subtitleKey: 'bundledDocuments.productChange',
        subtitleFallback: ' product change',
    },
};

const stripLocaleSuffix = (value: string): string => value.replace(/\s*\[[a-z]{2}\]$/i, '').trim();

// productId -> display label, built from the product filter options. Falls back to the set key suffix.
const getProductLabel = (set: DocumentSet, productLabels: Map<string, string>): string => {
    const label = productLabels.get(String(set.productId));

    if (label) return label;

    const suffix = set.key.split('_').slice(1).join(' ');

    return suffix || String(set.productId);
};

export interface AdaptBundleDocumentsParams {
    response: DocumentSetsResponse | undefined;
    t: TFunction;
    handleEdit: (bundleId: string) => void;
    productOptions?: ProductOption[];
}

export const adaptBundleDocuments = ({
    response,
    t,
    handleEdit,
    productOptions = [],
}: AdaptBundleDocumentsParams): BundleSection[] => {
    const sets = response?.documentSets ?? [];

    const productLabels = new Map(productOptions.map(({ value, label }) => [String(value), label]));

    const sectionsByProcess = new Map<number, BundleCardType[]>();

    sets.forEach((set) => {
        const config = PROCESS_CONFIG[set.process];

        if (!config) return;

        const orderedCategories = [...set.categories].sort((a, b) => a.orderIndex - b.orderIndex);

        const card: BundleCardType = {
            id: set.id,
            product: getProductLabel(set, productLabels),
            subtitle: set?.name,
            productId: String(set.productId),
            processType: config.processType,
            categories: orderedCategories?.map((category) => category?.key),
            documents: orderedCategories?.map((category) => stripLocaleSuffix(category.name)),
            onEdit: () => handleEdit(String(set.id)),
        };

        const cards = sectionsByProcess.get(set.process) ?? [];

        cards.push(card);
        sectionsByProcess.set(set.process, cards);
    });

    return Object.keys(PROCESS_CONFIG)
        .map(Number)
        .filter((process) => sectionsByProcess.has(process))
        .map((process) => {
            const config = PROCESS_CONFIG[process];

            return {
                sectionTitle: t(config.sectionKey, config.sectionFallback),
                cards: sectionsByProcess.get(process) ?? [],
            };
        });
};
